import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RxDocument } from 'rxdb'
import { useRxCollection, useRxData } from 'rxdb-hooks'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { buildClearSnoozePatch, buildSnoozePatch } from '~/boundedContext/cards/utils/card'
import { Card } from '~/db/types'
import { useCardDeletionRevert } from '~/hooks/use-card-deletion-revert'
import { useAccountStore } from '~/stores/account'
import { useCardsGameStore } from '~/stores/cards-game'
import { useSettingsStore } from '~/stores/settings'
import { buildListQuery } from '~/utils/build-list-query'

import { streamExample } from '../../utils/stream-example'
import { StatelessList } from '../stateless-list'

import { useScrollToLatest } from './hooks'
import { ListProps } from './types'

export const List = ({ className, direction = 'asc', activeCardId }: ListProps) => {
  const { t } = useTranslation()
  const accountId = useAccountStore(state => state.id)
  const learnLanguageValue = useSettingsStore(state => state.learnLanguage.value)
  const sourceLanguageValue = useSettingsStore(state => state.sourceLanguage.value)
  const learnLanguageLabel = useSettingsStore(state => state.learnLanguage.label)

  const { result: cards, isFetching } = useRxData<Card>('cards', collection =>
    collection.find({
      selector: buildListQuery(accountId, learnLanguageValue, sourceLanguageValue),
      sort: [{ createdAt: direction }],
    }),
  )
  const collectionCards = useRxCollection<Card>('cards')
  const [lastInsertedId, setLastInsertedId] = useState<string | null>(null)
  const cardsContainerRef = useRef<HTMLDivElement>(null)
  const activeCardRef = useRef<HTMLDivElement | null>(null)

  const removeCard = useCardDeletionRevert()

  useEffect(() => {
    if (!collectionCards) return

    const subscription = collectionCards.insert$.subscribe(event => {
      setLastInsertedId(event.documentId)
    })

    return () => subscription.unsubscribe()
  }, [collectionCards])

  useScrollToLatest(cards, isFetching, !!activeCardId, direction, cardsContainerRef)

  const onExamplesChange = useCallback((card: RxDocument<Card>, examples: string) => {
    void card.incrementalPatch({ examples })
  }, [])
  const onLearnTextChange = useCallback((card: RxDocument<Card>, learnText: string) => {
    void card.incrementalPatch({ learnText })
  }, [])
  const onSourceTextChange = useCallback((card: RxDocument<Card>, sourceText: string) => {
    void card.incrementalPatch({ sourceText })
  }, [])
  const onPronunciationChange = useCallback((card: RxDocument<Card>, pronunciation: string) => {
    void card.incrementalPatch({ pronunciation })
  }, [])
  const onUnsnooze = useCallback((card: RxDocument<Card>) => {
    void card.incrementalPatch(buildClearSnoozePatch())
  }, [])
  const onSnoozeForCount = useCallback(async (card: RxDocument<Card>, snoozeCount: number) => {
    await card.incrementalPatch(buildSnoozePatch(snoozeCount))
    useCardsGameStore.getState().onRemove(card.id)
  }, [])
  const refreshPronunciation = useCallback(
    async (card: RxDocument<Card>) => {
      if (!learnLanguageLabel) return

      try {
        const { data: { pronunciation } = {} } = await apiClient.GET('/pronunciation', {
          params: {
            query: { language: learnLanguageLabel ?? 'en', text: card.learnText },
          },
        })
        if (pronunciation) await card.incrementalPatch({ pronunciation })
      } catch (error) {
        toast.error(t('Network error'))
        console.error({ message: 'Network error', error })
        return
      }
    },
    [learnLanguageLabel, t],
  )
  const onRefresh = useCallback(
    async (card: RxDocument<Card>) => {
      if (!learnLanguageLabel) return

      const languageQuery = `&language=${learnLanguageLabel}`
      const refreshTasks = [streamExample(card, card.learnText, card.sourceText, languageQuery)]

      if (!card.pronunciation?.trim()) refreshTasks.push(refreshPronunciation(card))

      await Promise.all(refreshTasks)
    },
    [learnLanguageLabel, refreshPronunciation],
  )

  return (
    <StatelessList
      ref={cardsContainerRef}
      activeCardId={activeCardId}
      activeCardRef={activeCardRef}
      cards={cards}
      className={className}
      lastInsertedId={lastInsertedId}
      onExamplesChange={onExamplesChange}
      onLearnTextChange={onLearnTextChange}
      onPronunciationChange={onPronunciationChange}
      onRefresh={onRefresh}
      onRemove={removeCard}
      onSnoozeForCount={onSnoozeForCount}
      onSourceTextChange={onSourceTextChange}
      onUnsnooze={onUnsnooze}
    />
  )
}
